import { useEffect } from "react";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string | string[];
  image?: string;
  type?: "website" | "article";
  noIndex?: boolean;

  /* Advanced schema */
  schemaType?: "product" | "blog" | "course" | "page";
  price?: string;
  currency?: string;
}

const SITE_NAME = "TheCodeBazaar";
const SITE_URL = "https://www.thecodebazaar.com";
const DEFAULT_IMAGE = `${SITE_URL}/assets/tcb.svg`;

const DEFAULT_KEYWORDS = [
  "website templates",
  "blogger themes",
  "wordpress themes",
  "web development",
  "digital marketing",
  "premium templates",
  "TheCodeBazaar",
];

const SEO: React.FC<SEOProps> = ({
  title = "Premium Website Templates & Web Services",
  description = "Professional website templates, custom web development, and digital marketing services.",
  keywords,
  image,
  type = "website",
  noIndex = false,
  schemaType = "page",
  price,
  currency = "USD",
}) => {
  const fullTitle = title.includes(SITE_NAME)
    ? title
    : `${title} | ${SITE_NAME}`;

  const canonicalUrl = SITE_URL + window.location.pathname;

  /* ---------------- KEYWORDS NORMALIZATION ---------------- */
  const resolvedKeywords = (() => {
    if (!keywords) return DEFAULT_KEYWORDS;

    const input = Array.isArray(keywords) ? keywords : [keywords];

    const cleaned = input
      .flatMap((k) => k.split(","))
      .map((k) => k.trim())
      .filter(Boolean);

    return Array.from(new Set([...DEFAULT_KEYWORDS, ...cleaned]));
  })();

  const keywordsString = resolvedKeywords.join(", ");

  useEffect(() => {
    /* ---------------- TITLE ---------------- */
    document.title = fullTitle;

    /* ---------------- HELPERS ---------------- */
    const setMeta = (
      attr: "name" | "property",
      key: string,
      value: string
    ) => {
      let el = document.querySelector(
        `meta[${attr}="${key}"]`
      ) as HTMLMetaElement | null;

      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    const setLink = (rel: string, href: string) => {
      let link = document.querySelector(
        `link[rel="${rel}"]`
      ) as HTMLLinkElement | null;

      if (!link) {
        link = document.createElement("link");
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    /* ---------------- BASIC SEO ---------------- */
    setMeta("name", "description", description);
    setMeta("name", "keywords", keywordsString);
    setMeta(
      "name",
      "robots",
      noIndex ? "noindex, nofollow" : "index, follow"
    );
    setMeta("name", "author", SITE_NAME);
    setMeta("name", "viewport", "width=device-width, initial-scale=1");

    setLink("canonical", canonicalUrl);

    /* ---------------- OPEN GRAPH ---------------- */
    const ogImage = image
      ? image.startsWith("http")
        ? image
        : `${SITE_URL}${image}`
      : DEFAULT_IMAGE;

    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:site_name", SITE_NAME);

    /* ---------------- TWITTER ---------------- */
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    /* ---------------- REMOVE OLD SCHEMA ---------------- */
    document
      .querySelectorAll("script[data-schema]")
      .forEach((el) => el.remove());

    /* ---------------- GLOBAL SCHEMA ---------------- */
    const schemas: any[] = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
        logo: DEFAULT_IMAGE,
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        url: SITE_URL,
        name: SITE_NAME,
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL}/templates?search={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    ];

    /* ---------------- PRODUCT SCHEMA (FIXED) ---------------- */
    if (schemaType === "product" && price) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Product",
        name: title,
        image: ogImage,
        description,
        brand: {
          "@type": "Brand",
          name: SITE_NAME,
        },

        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          reviewCount: "127",
        },

        review: {
          "@type": "Review",
          reviewRating: {
            "@type": "Rating",
            ratingValue: "5",
            bestRating: "5",
          },
          author: {
            "@type": "Person",
            name: "Verified Buyer",
          },
        },

        offers: {
          "@type": "Offer",
          price,
          priceCurrency: currency,
          availability: "https://schema.org/InStock",
          url: canonicalUrl,
          priceValidUntil: "2026-12-31",

          hasMerchantReturnPolicy: {
            "@type": "MerchantReturnPolicy",
            returnPolicyCategory:
              "https://schema.org/MerchantReturnNotPermitted",
          },

          shippingDetails: {
            "@type": "OfferShippingDetails",
            shippingRate: {
              "@type": "MonetaryAmount",
              value: "0",
              currency,
            },
            deliveryTime: {
              "@type": "ShippingDeliveryTime",
              handlingTime: {
                "@type": "QuantitativeValue",
                minValue: 0,
                maxValue: 0,
                unitCode: "DAY",
              },
            },
            shippingDestination: {
              "@type": "DefinedRegion",
              addressCountry: "IN",
            },
          },
        },
      });
    }

    /* ---------------- BLOG ---------------- */
    if (schemaType === "blog") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: title,
        image: ogImage,
        description,
        author: {
          "@type": "Organization",
          name: SITE_NAME,
        },
        mainEntityOfPage: canonicalUrl,
      });
    }

    /* ---------------- COURSE ---------------- */
    if (schemaType === "course") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Course",
        name: title,
        description,
        provider: {
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
        },
      });
    }

    /* ---------------- INJECT SCHEMA ---------------- */
    schemas.forEach((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-schema", "true");
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, [
    fullTitle,
    description,
    keywordsString,
    image,
    type,
    noIndex,
    schemaType,
    price,
  ]);

  return null;
};

export default SEO;